import {
  Alert,
  Button,
  Col,
  FloatingLabel,
  Form,
  Row,
} from 'react-bootstrap'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import moment from 'moment'
import {
  GetLaboratoryCollectionPointDocument,
  GetLaboratoryCollectionPointQuery,
  GetLaboratoryCollectionPointQueryVariables,
  useInsertLaboratoryCollectionPointMutation,
  useUpdateLaboratoryCollectionPointMutation,
} from '../generated/urql.administrator'
import { assertPoint, Point as PointType } from '../lib/geography'
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api'
import { GoogleApiKey } from '../const'
import * as Time from '../lib/time'
import { useClient } from 'urql'

type OpeningHoursWithSpecialDays = Time.WeekSchedule & {
  specialClosedDays?: string[]
}

type FormLaboratoryCollectionPoint = {
  name: string
  address: string
  postalCode: string
  city: string
  location: PointType['coordinates']
  openingHours: Time.WeekSchedule
  specialClosedDays: string[]
  isVoucherEnabled: boolean
  isExternalResults: boolean
  email: string
}

type LaboratoryCollectionPointFormProps = {
  laboratoryId?: number
  id?: number
  onSuccess: () => void
  submitButtonLabel?: string
}



const LaboratoryCollectionPointForm = ({
  id,
  laboratoryId,
  onSuccess,
  submitButtonLabel = 'Zapisz',
}: LaboratoryCollectionPointFormProps) => {
  const client = useClient()
  const [, setFetchingPoint] = useState(false)
  const [
    { fetching: updateFetching },
    updateLaboratoryCollectionPoint,
  ] = useUpdateLaboratoryCollectionPointMutation()
  const [
    { fetching: insertFetching },
    insertLaboratoryCollectionPoint,
  ] = useInsertLaboratoryCollectionPointMutation()
  const [generalError, setGeneralError] = useState<string>()
  const [zoom, setZoom] = useState(15)
  const [specialClosedDayInput, setSpecialClosedDayInput] = useState('')

  const defaultValues = useMemo<FormLaboratoryCollectionPoint>(
    () => ({
      name: '',
      address: '',
      postalCode: '',
      city: '',
      location: [52, 16],
      openingHours: {},
      specialClosedDays: [],
      isVoucherEnabled: false,
      isExternalResults: false,
      email: '',
    }),
    []
  )

  const {
    handleSubmit,
    reset,
    formState: { errors },
    register,
    control,
    setValue,
    watch,
  } = useForm<FormLaboratoryCollectionPoint>({
    defaultValues,
  })

  useEffect(() => {
    reset(defaultValues)
    setSpecialClosedDayInput('')
  }, [defaultValues, id, laboratoryId, reset])

  const onZoomChanged = useCallback(function () {
    // @ts-ignore
    setZoom((this as GoogleMap).getZoom())
  }, [])

  useEffect(() => {
    if (id) {
      setFetchingPoint(true)
      client
        .query<
          GetLaboratoryCollectionPointQuery,
          GetLaboratoryCollectionPointQueryVariables
        >(
          GetLaboratoryCollectionPointDocument,
          {
            id,
          },
          { requestPolicy: 'network-only' }
        )
        .toPromise()
        .then(({ data }) => {
          if (data?.laboratory_collection_point_by_pk) {
            const location = data.laboratory_collection_point_by_pk.location || {
              type: 'Point',
              coordinates: [52, 16],
            }

            assertPoint(location)

            const openingHours =
              (data.laboratory_collection_point_by_pk
                .openingHours as OpeningHoursWithSpecialDays) || {}
            const { specialClosedDays = [], ...weekSchedule } = openingHours

            reset({
              name: data.laboratory_collection_point_by_pk.name || '',
              address: data.laboratory_collection_point_by_pk.address || '',
              postalCode:
                data.laboratory_collection_point_by_pk.postalCode || '',
              city: data.laboratory_collection_point_by_pk.city || '',
              location: location.coordinates,
              openingHours: weekSchedule,
              specialClosedDays,
              isVoucherEnabled:
                data.laboratory_collection_point_by_pk.isVoucherEnabled ||
                false,
              isExternalResults:
                data.laboratory_collection_point_by_pk.isExternalResults ||
                false,
              email:
                data.laboratory_collection_point_by_pk.email || '',
            })
          }
        })
        .finally(() => setFetchingPoint(false))
    }
  }, [client, id, reset])

  const specialClosedDays = watch('specialClosedDays') || []

  const handleAddSpecialClosedDay = () => {
    if (!specialClosedDayInput) {
      return
    }

    if (specialClosedDays.includes(specialClosedDayInput)) {
      setSpecialClosedDayInput('')
      return
    }

    setValue('specialClosedDays', [...specialClosedDays, specialClosedDayInput])
    setSpecialClosedDayInput('')
  }

  const handleRemoveSpecialClosedDay = (day: string) => {
    setValue(
      'specialClosedDays',
      specialClosedDays.filter((d) => d !== day)
    )
  }

  async function doSaveLaboratoryCollectionPoint({
    location,
    openingHours,
    specialClosedDays,
    isVoucherEnabled,
    isExternalResults,
    email,
    ...laboratoryCollectionPoint
  }: FormLaboratoryCollectionPoint) {
    setGeneralError(undefined)

    if (!laboratoryId) {
      setGeneralError('Missing laboratoryId')
      return
    }

    const openingHoursPayload: OpeningHoursWithSpecialDays =
      specialClosedDays.length > 0
        ? { ...openingHours, specialClosedDays }
        : openingHours

    const { error } = id
      ? await updateLaboratoryCollectionPoint({
          ...laboratoryCollectionPoint,
          location: {
            type: 'Point',
            coordinates: location,
          },
          openingHours: openingHoursPayload,
          isVoucherEnabled,
          isExternalResults,
          email: email || null,
          id,
        })
      : await insertLaboratoryCollectionPoint({
          ...laboratoryCollectionPoint,
          location: {
            type: 'Point',
            coordinates: location,
          },
          openingHours: openingHoursPayload,
          isVoucherEnabled,
          isExternalResults,
          email: email || null,
          laboratoryId,
        })

    if (error) {
      setGeneralError(error.message)
      return
    }

    onSuccess()
  }

  return (
    <Form onSubmit={handleSubmit(doSaveLaboratoryCollectionPoint)}>
      <LoadScript googleMapsApiKey={GoogleApiKey as string} version="3.64">
        <div className="mt-3">
          {generalError && <Alert variant="danger">{generalError}</Alert>}

          <Row>
            <Col>
              <h4>Dane punktu pobrań</h4>
              <FloatingLabel label="Nazwa" className="mb-3">
                <Form.Control
                  isInvalid={!!errors.name}
                  type="name"
                  placeholder="Nazwa"
                  {...register('name', {
                    required: 'Proszę podać nazwę',
                  })}
                />
                {errors.name && (
                  <Form.Control.Feedback>
                    {errors.name.message}
                  </Form.Control.Feedback>
                )}
              </FloatingLabel>

              <FloatingLabel label="Adres" className="mb-3">
                <Form.Control
                  isInvalid={!!errors.address}
                  type="text"
                  placeholder="Adres"
                  {...register('address', {
                    required: 'Proszę podać adres',
                  })}
                />
                {errors.address && (
                  <Form.Control.Feedback>
                    {errors.address.message}
                  </Form.Control.Feedback>
                )}
              </FloatingLabel>

              <FloatingLabel label="Kod pocztowy" className="mb-3">
                <Form.Control
                  isInvalid={!!errors.postalCode}
                  type="text"
                  placeholder="Kod pocztowy"
                  {...register('postalCode', {
                    required: 'Proszę podać kod pocztowy',
                    pattern: {
                      value: /^[0-9]{2}-[0-9]{3}$/,
                      message: 'Nieprowidłowy kod pocztowy',
                    },
                  })}
                />
                {errors.postalCode && (
                  <Form.Control.Feedback>
                    {errors.postalCode.message}
                  </Form.Control.Feedback>
                )}
              </FloatingLabel>

              <FloatingLabel label="Miasto" className="mb-3">
                <Form.Control
                  isInvalid={!!errors.city}
                  type="text"
                  placeholder="Miasto"
                  {...register('city', {
                    required: 'Proszę podać miasto',
                  })}
                />
                {errors.city && (
                  <Form.Control.Feedback>
                    {errors.city.message}
                  </Form.Control.Feedback>
                )}
              </FloatingLabel>

              <Form.Check
                type="checkbox"
                id="isVoucherEnabled"
                label="Realizacja voucher"
                className="mb-3"
                {...register('isVoucherEnabled')}
              />

              <Form.Check
                type="checkbox"
                id="isExternalResults"
                label="Wyniki na zewnątrz"
                className="mb-3"
                {...register('isExternalResults')}
              />

              <FloatingLabel label="E-mail powiadomień" className="mb-3">
                <Form.Control
                  type="email"
                  placeholder="E-mail powiadomień"
                  {...register('email')}
                />
              </FloatingLabel>

              <h4>Godziny otwarcia</h4>

              {(
                ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
              ).map((weekDay) => (
                <Row className="mb-1" key={weekDay}>
                  <Col className="d-flex align-items-center">
                    {Time.WeekDay2Name[weekDay]}
                  </Col>
                  <Col className="d-flex align-items-center">
                    <Form.Control
                      type="time"
                      disabled={!watch(`openingHours.${weekDay}`)}
                      step={1}
                      value={watch(`openingHours.${weekDay}.0`)}
                      onChange={(event) =>
                        setValue(
                          `openingHours.${weekDay}.0`,
                          event.target.value as Time.Time
                        )
                      }
                    />
                    &nbsp;-&nbsp;
                    <Form.Control
                      type="time"
                      disabled={!watch(`openingHours.${weekDay}`)}
                      step={1}
                      value={watch(`openingHours.${weekDay}.1`)}
                      onChange={(event) =>
                        setValue(
                          `openingHours.${weekDay}.1`,
                          event.target.value as Time.Time
                        )
                      }
                    />
                  </Col>

                  <Col className="d-flex align-items-center">
                    <Form.Check
                      checked={!watch(`openingHours.${weekDay}`)}
                      onChange={(event) => {
                        setValue(
                          `openingHours.${weekDay}`,
                          event.target.checked
                            ? undefined
                            : ['00:00:00', '23:59:59']
                        )
                      }}
                    />
                    &nbsp;Zamknięte
                  </Col>
                </Row>
              ))}
            </Col>
            <Col>
              <h4>Lokalizacja</h4>

              <Controller
                name="location"
                control={control}
                render={({ field: { value: location } }) => {
                  return (
                    <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '400px' }}
                      center={{ lat: location[0], lng: location[1] }}
                      zoom={zoom}
                      onZoomChanged={onZoomChanged}
                    >
                      <Marker
                        position={{ lat: location[0], lng: location[1] }}
                        draggable
                        onDragEnd={(event) => {
                          if (event.latLng) {
                            setValue('location', [
                              event.latLng.lat(),
                              event.latLng.lng(),
                            ])
                          }
                        }}
                      />
                    </GoogleMap>
                  )
                }}
              />

              <div className="d-flex flex-column gap-2 mt-3">
                <div className="d-flex">
                  <h4>Zablokowane dni</h4>
                </div>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="date"
                    value={specialClosedDayInput}
                    onChange={(event) =>
                      setSpecialClosedDayInput(event.target.value)
                    }
                  />
                  <Button
                    type="button"
                    disabled={!specialClosedDayInput}
                    onClick={handleAddSpecialClosedDay}
                  >
                    Dodaj
                  </Button>
                </div>
                {specialClosedDays.length > 0 && (
                  <div className="mt-3">
                    <div className="d-flex flex-column gap-2">
                      {specialClosedDays.map((day) => (
                        <div
                          key={day}
                          className="d-flex justify-content-between align-items-center border rounded px-3 py-2"
                        >
                            <span>
                              {moment(day, 'YYYY-MM-DD', true).isValid()
                                ? moment(day).format('DD.MM.YYYY')
                                : day}
                            </span>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            type="button"
                            onClick={() => handleRemoveSpecialClosedDay(day)}
                          >
                            Usuń
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </LoadScript>
      <div className="d-flex justify-content-end mt-3">
        <Button
          variant="success"
          type="submit"
          disabled={updateFetching || insertFetching}
        >
          {submitButtonLabel}
        </Button>
      </div>
    </Form>
  )
}

export default LaboratoryCollectionPointForm

